import React from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { Progress } from "../ui/progress";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../ui/tabs";
import { Checkbox } from "../ui/checkbox";
import { Textarea } from "../ui/textarea";
import {
  Package,
  FileCheck,
  ClipboardList,
  Wrench,
  CheckCircle2,
  Lock,
  User,
  ChevronRight,
  AlertCircle,
  Undo2,
  RefreshCw,
  ArrowRight,
  XCircle,
} from "lucide-react";

/**
 * ProvisionDetailModal — Diálogo de detalle/gestión de una provisión.
 *
 * Extraído 1:1 desde `pages/ProvisionsPage.js` (es el modal más pesado de la página).
 * Muestra la cabecera de la provisión, los productos a provisionar, las asignaciones
 * (ejecutivo, gestor, técnico) y las tres fases (Comercial / Administrativa / Técnica)
 * con sus checklists, progreso y acciones de avance/retroceso/cancelación/instalación.
 *
 * @param {boolean}     props.detailModalOpen        Apertura del diálogo.
 * @param {Function}    props.setDetailModalOpen      Setter del flag de apertura.
 * @param {Object|null} props.selectedProvision       Provisión seleccionada (null = sin datos).
 * @param {Function}    props.setSelectedProvision    Setter de la provisión seleccionada (edición inline de comentarios).
 * @param {Object}      props.statusColors            Mapa estado→clases de color del badge.
 * @param {Object}      props.statusLabels            Mapa estado→etiqueta legible.
 * @param {Function}    props.formatDate              (dateStr) => string — formatea fechas.
 * @param {boolean}     props.canEditProvisions       Permiso RBAC de edición de provisiones.
 * @param {Array}       props.comerciales             Usuarios comerciales asignables.
 * @param {Array}       props.gestores                Usuarios gestores asignables.
 * @param {Array}       props.tecnicos                Usuarios técnicos asignables.
 * @param {string}      props.activeTab               Fase activa ("comercial" | "administrativa" | "tecnica").
 * @param {Function}    props.setActiveTab            Cambia la fase activa.
 * @param {Object}      props.user                    Usuario autenticado (para checks de rol).
 * @param {Object}      props.expandedItems           Mapa itemId→bool de items técnicos expandidos.
 * @param {Function}    props.toggleItemExpanded      (itemId) => void — expande/colapsa un item técnico.
 * @param {Function}    props.canEditTab              (tabName) => boolean — permiso de edición por fase.
 * @param {Function}    props.canAdvance              () => boolean — si se puede avanzar de fase.
 * @param {Function}    props.setRollbackModalOpen    Abre el modal de retroceso de fase.
 * @param {Function}    props.setCancelModalOpen      Abre el modal de cancelación.
 * @param {Function}    props.setInstallItemId        Fija el item técnico a instalar.
 * @param {Function}    props.setInstallDate          Fija la fecha de instalación (reset).
 * @param {Function}    props.setInstallModalOpen     Abre el modal de instalación.
 * @param {Function}    props.handleAdvancePhase      Avanza la provisión a la siguiente fase.
 * @param {Function}    props.handleAssignmentUpdate  (field, value) => void — asigna ejecutivo/gestor.
 * @param {Function}    props.handleAssignTechnician  (techId) => void — asigna técnico.
 * @param {Function}    props.handleRefreshChecklist  Recarga las tareas maestras de la fase.
 * @param {Function}    props.handleChecklistUpdate   (phase, index, completed, comments?) => void.
 * @param {Function}    props.handleToggleAllChecklist (phase, checked) => void — marca todas.
 * @param {Function}    props.handleTechnicalItemChecklistUpdate (itemId, taskId, checked) => void.
 * @param {Function}    props.handleCompleteProvision Completa la provisión (fase técnica).
 */
export const ProvisionDetailModal = ({
  detailModalOpen,
  setDetailModalOpen,
  selectedProvision,
  setSelectedProvision,
  statusColors,
  statusLabels,
  formatDate,
  canEditProvisions,
  comerciales,
  gestores,
  tecnicos,
  activeTab,
  setActiveTab,
  user,
  expandedItems,
  toggleItemExpanded,
  canEditTab,
  canAdvance,
  setRollbackModalOpen,
  setCancelModalOpen,
  setInstallItemId,
  setInstallDate,
  setInstallModalOpen,
  handleAdvancePhase,
  handleAssignmentUpdate,
  handleAssignTechnician,
  handleRefreshChecklist,
  handleChecklistUpdate,
  handleToggleAllChecklist,
  handleTechnicalItemChecklistUpdate,
  handleCompleteProvision,
}) => {
  return (
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="bg-[#0A0A35] border-[#1E1E5F] text-white max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-white font-heading flex items-center gap-2">
              <Package className="h-5 w-5 text-[#03BEE4]" />
              Provisión {selectedProvision?.provision_id}
            </DialogTitle>
          </DialogHeader>
          
          {selectedProvision && (
            <div className="space-y-6 mt-4">
              {/* Header Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-slate-400 text-xs">Cliente</p>
                  <p className="text-white font-medium">{selectedProvision.customer_name}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-slate-400 text-xs">Ubicación</p>
                  <p className="text-white font-medium">{selectedProvision.location_name}</p>
                </div>
                <div className="p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-slate-400 text-xs">Estado</p>
                  <Badge className={`${statusColors[selectedProvision.status]} text-white mt-1`}>
                    {statusLabels[selectedProvision.status]}
                  </Badge>
                </div>
                <div className="p-3 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-slate-400 text-xs">Fecha Programada</p>
                  <p className="text-white font-medium">{formatDate(selectedProvision.scheduled_date)}</p>
                </div>
              </div>

              {/* Cancellation Reason - Show when provision is canceled */}
              {selectedProvision.status === "CANCELADA" && selectedProvision.cancel_reason && (
                <div className="p-4 rounded-lg bg-red-500/10 border border-red-500/30">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-red-400 font-medium mb-1">Motivo de Cancelación</p>
                      <p className="text-slate-300">{selectedProvision.cancel_reason}</p>
                      {selectedProvision.cancelled_by_name && (
                        <p className="text-xs text-slate-500 mt-2">
                          Cancelada por {selectedProvision.cancelled_by_name} el {formatDate(selectedProvision.cancelled_at)}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* Products/Services - MOVED TO TOP */}
              {selectedProvision.products?.length > 0 && (
                <Card className="bg-[#1B1B4B]/30 border-[#1E1E5F]">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-[#03BEE4] text-lg flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Productos/Servicios a Provisionar
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {selectedProvision.products.map((product, index) => (
                        <div 
                          key={index}
                          className="flex items-center justify-between p-3 rounded-lg bg-[#0A0A35] border border-[#1E1E5F]"
                        >
                          <div>
                            <p className="text-white font-medium">{product.service_name}</p>
                            <p className="text-xs text-slate-400">{product.product_type}</p>
                          </div>
                          <div className="text-right">
                            <p className="text-white">x{product.quantity}</p>
                            {product.precio > 0 && (
                              <p className="text-xs text-slate-400">{product.precio}€/mes</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Action Buttons - Conditional per phase */}
              <div className="flex justify-between items-center">
                <div className="flex gap-2">
                  {/* Devolver a Fase Anterior - ONLY in Administrativa and Técnica */}
                  {(selectedProvision.status === "PENDIENTE_ADMINISTRATIVA" || selectedProvision.status === "PENDIENTE_TECNICA") && (
                    <Button
                      onClick={() => setRollbackModalOpen(true)}
                      variant="outline"
                      className="border-orange-500 text-orange-400 hover:bg-orange-500/20"
                      data-testid="rollback-provision-btn"
                    >
                      <Undo2 className="h-4 w-4 mr-2" />
                      Devolver a Fase Anterior
                    </Button>
                  )}
                  
                  {/* Cancelar Provisión - Available in all active phases */}
                  {(selectedProvision.status !== "COMPLETADA" && selectedProvision.status !== "CANCELADA") && canEditProvisions && (
                    <Button
                      onClick={() => setCancelModalOpen(true)}
                      variant="outline"
                      className="border-red-500 text-red-400 hover:bg-red-500/20"
                      data-testid="cancel-provision-btn"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancelar Provisión
                    </Button>
                  )}
                </div>
                
                {/* Pasar a Siguiente Fase - ONLY in Comercial and Administrativa */}
                {(selectedProvision.status === "PENDIENTE_COMERCIAL" || selectedProvision.status === "PENDIENTE_ADMINISTRATIVA") ? (
                  <Button
                    onClick={handleAdvancePhase}
                    disabled={!canAdvance() || !canEditProvisions}
                    className={`${canAdvance() 
                      ? 'bg-[#42E695] text-[#04042C] hover:bg-[#34D399]' 
                      : 'bg-slate-600 text-slate-400 cursor-not-allowed'}`}
                    data-testid="advance-provision-btn"
                  >
                    <ArrowRight className="h-4 w-4 mr-2" />
                    Pasar a Siguiente Fase
                  </Button>
                ) : selectedProvision.status === "PENDIENTE_TECNICA" ? (
                  <div className="text-sm text-slate-400 bg-[#1B1B4B]/50 px-4 py-2 rounded-lg border border-[#1E1E5F]">
                    <AlertCircle className="h-4 w-4 inline mr-2 text-orange-400" />
                    Fase técnica — Complete la instalación de servicios
                  </div>
                ) : <div />}
              </div>

              {/* Assignments - 3 columns with same style */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="p-4 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-blue-400 text-sm font-medium mb-2 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Ejecutivo Comercial
                  </p>
                  <Select
                    value={selectedProvision.ejecutivo_id || "unassigned"}
                    onValueChange={(value) => handleAssignmentUpdate("ejecutivo_id", value === "unassigned" ? null : value)}
                    disabled={!canEditProvisions}
                  >
                    <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      <SelectItem value="unassigned" className="text-slate-400">Sin asignar</SelectItem>
                      {comerciales.map(u => (
                        <SelectItem key={u.id} value={u.id} className="text-white">
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="p-4 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-yellow-400 text-sm font-medium mb-2 flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Gestor Administrativo
                  </p>
                  <Select
                    value={selectedProvision.gestor_id || "unassigned"}
                    onValueChange={(value) => handleAssignmentUpdate("gestor_id", value === "unassigned" ? null : value)}
                    disabled={!canEditProvisions}
                  >
                    <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      <SelectItem value="unassigned" className="text-slate-400">Sin asignar</SelectItem>
                      {gestores.map(u => (
                        <SelectItem key={u.id} value={u.id} className="text-white">
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="p-4 rounded-lg bg-[#1B1B4B]/50 border border-[#1E1E5F]">
                  <p className="text-orange-400 text-sm font-medium mb-2 flex items-center gap-2">
                    <Wrench className="h-4 w-4" />
                    Técnico
                  </p>
                  <Select
                    value={selectedProvision.assigned_technician_id || "unassigned"}
                    onValueChange={(value) => handleAssignTechnician(value === "unassigned" ? null : value)}
                    disabled={!canEditProvisions}
                  >
                    <SelectTrigger className="bg-[#0A0A35] border-[#1E1E5F] text-white">
                      <SelectValue placeholder="Sin asignar" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#0A0A35] border-[#1E1E5F]">
                      <SelectItem value="unassigned" className="text-slate-400">Sin asignar</SelectItem>
                      {tecnicos.map(u => (
                        <SelectItem key={u.id} value={u.id} className="text-white">
                          {u.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Phase Tabs */}
              <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                <TabsList className="grid w-full grid-cols-3 bg-[#1B1B4B]">
                  <TabsTrigger 
                    value="comercial" 
                    className={`data-[state=active]:bg-blue-500 data-[state=active]:text-white ${
                      canEditTab("comercial") ? "" : "opacity-70"
                    }`}
                  >
                    <FileCheck className="h-4 w-4 mr-2" />
                    Comercial ({selectedProvision.comercial_progress}%)
                    {selectedProvision.comercial_progress === 100 && <CheckCircle2 className="h-3 w-3 ml-1 text-green-400" />}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="administrativa"
                    className={`data-[state=active]:bg-yellow-500 data-[state=active]:text-white ${
                      canEditTab("administrativa") ? "" : selectedProvision.comercial_progress < 100 ? "opacity-50" : "opacity-70"
                    }`}
                  >
                    <ClipboardList className="h-4 w-4 mr-2" />
                    Administrativa ({selectedProvision.administrativa_progress}%)
                    {selectedProvision.administrativa_progress === 100 && <CheckCircle2 className="h-3 w-3 ml-1 text-green-400" />}
                    {selectedProvision.comercial_progress < 100 && <Lock className="h-3 w-3 ml-1 text-slate-500" />}
                  </TabsTrigger>
                  <TabsTrigger 
                    value="tecnica"
                    className={`data-[state=active]:bg-orange-500 data-[state=active]:text-white ${
                      canEditTab("tecnica") ? "" : selectedProvision.administrativa_progress < 100 ? "opacity-50" : "opacity-70"
                    }`}
                  >
                    <Wrench className="h-4 w-4 mr-2" />
                    Técnica
                    {selectedProvision.administrativa_progress < 100 && <Lock className="h-3 w-3 ml-1 text-slate-500" />}
                  </TabsTrigger>
                </TabsList>

                {/* Commercial Phase */}
                <TabsContent value="comercial" className="mt-4">
                  <Card className="bg-[#1B1B4B]/30 border-[#1E1E5F]">
                    <CardHeader>
                      <CardTitle className="text-blue-400 text-lg flex items-center gap-2">
                        <FileCheck className="h-5 w-5" />
                        Checklist Fase Comercial
                        {!canEditTab("comercial") && (
                          <span className="text-xs text-slate-400 font-normal ml-2">(Solo lectura)</span>
                        )}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Rollback Message - Show if provision was rolled back to this phase */}
                      {selectedProvision.status === "PENDIENTE_COMERCIAL" && selectedProvision.rollback_reason && (
                        <div className="mb-4 p-3 rounded-lg bg-orange-500/20 border border-orange-500/50">
                          <p className="text-orange-400 text-sm font-medium flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Motivo del retroceso:
                          </p>
                          <p className="text-white mt-1">{selectedProvision.rollback_reason}</p>
                        </div>
                      )}
                      
                      {selectedProvision.comercial_checklist?.length === 0 ? (
                        <div className="text-center py-4 space-y-3">
                          <p className="text-slate-400">
                            No hay tareas definidas para esta fase.
                          </p>
                          {(user?.roles?.some(r => r === "SUPERADMIN" || r === "GESTION")) && (
                            <Button
                              onClick={handleRefreshChecklist}
                              variant="outline"
                              className="border-blue-500 text-blue-400 hover:bg-blue-500/10"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Cargar Tareas del Maestro
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Select All Checkbox */}
                          <div className="flex items-center gap-3 p-2 rounded-lg bg-[#0A0A35]/50 border border-[#1E1E5F]">
                            <Checkbox
                              checked={selectedProvision.comercial_checklist?.every(item => item.completed)}
                              onCheckedChange={(checked) => handleToggleAllChecklist("comercial", checked)}
                              className="border-blue-400 data-[state=checked]:bg-blue-500"
                              disabled={!canEditTab("comercial")}
                            />
                            <span className="text-blue-400 font-medium text-sm">Seleccionar Todos</span>
                            {!canEditTab("comercial") && <Lock className="h-3 w-3 text-slate-500 ml-auto" />}
                          </div>
                          
                          {selectedProvision.comercial_checklist?.map((item, index) => (
                            <div 
                              key={item.task_id}
                              className={`p-3 rounded-lg border ${item.completed ? "bg-blue-500/10 border-blue-500/30" : "bg-[#0A0A35] border-[#1E1E5F]"}`}
                            >
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  checked={item.completed}
                                  onCheckedChange={(checked) => handleChecklistUpdate("comercial", index, checked)}
                                  className="mt-1 border-blue-400 data-[state=checked]:bg-blue-500"
                                  disabled={!canEditTab("comercial")}
                                />
                                <div className="flex-1">
                                  <p className={`font-medium ${item.completed ? "text-blue-400 line-through" : "text-white"}`}>
                                    {item.task_name}
                                  </p>
                                  {item.completed && item.completed_by_name && (
                                    <p className="text-xs text-slate-400 mt-1">
                                      Completado por {item.completed_by_name} el {formatDate(item.completed_at)}
                                    </p>
                                  )}
                                  <Textarea
                                    placeholder="Comentarios (opcional)"
                                    value={item.comments || ""}
                                    onChange={(e) => {
                                      const updated = [...selectedProvision.comercial_checklist];
                                      updated[index] = { ...updated[index], comments: e.target.value };
                                      setSelectedProvision({ ...selectedProvision, comercial_checklist: updated });
                                    }}
                                    onBlur={() => handleChecklistUpdate("comercial", index, item.completed, selectedProvision.comercial_checklist[index].comments)}
                                    className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm h-16"
                                    disabled={!canEditTab("comercial")}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-4">
                        <Progress value={selectedProvision.comercial_progress} className="h-2" />
                        <p className="text-sm text-slate-400 mt-1 text-right">
                          {selectedProvision.comercial_progress}% completado
                        </p>
                      </div>
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Administrative Phase */}
                <TabsContent value="administrativa" className="mt-4">
                  <Card className="bg-[#1B1B4B]/30 border-[#1E1E5F]">
                    <CardHeader>
                      <CardTitle className="text-yellow-400 text-lg flex items-center gap-2">
                        <ClipboardList className="h-5 w-5" />
                        Checklist Fase Administrativa
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {/* Rollback Message - Show if provision was rolled back to this phase */}
                      {selectedProvision.status === "PENDIENTE_ADMINISTRATIVA" && selectedProvision.rollback_reason && (
                        <div className="mb-4 p-3 rounded-lg bg-orange-500/20 border border-orange-500/50">
                          <p className="text-orange-400 text-sm font-medium flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Motivo del retroceso:
                          </p>
                          <p className="text-white mt-1">{selectedProvision.rollback_reason}</p>
                        </div>
                      )}
                      
                      {selectedProvision.administrativa_checklist?.length === 0 ? (
                        <div className="text-center py-4 space-y-3">
                          <p className="text-slate-400">
                            No hay tareas definidas para esta fase.
                          </p>
                          {(user?.roles?.some(r => r === "SUPERADMIN" || r === "GESTION")) && (
                            <Button
                              onClick={handleRefreshChecklist}
                              variant="outline"
                              className="border-yellow-500 text-yellow-400 hover:bg-yellow-500/10"
                            >
                              <RefreshCw className="h-4 w-4 mr-2" />
                              Cargar Tareas del Maestro
                            </Button>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-3">
                          {/* Blocked message if comercial not complete */}
                          {selectedProvision.comercial_progress < 100 && (
                            <div className="p-3 rounded-lg bg-slate-500/20 border border-slate-500/30 text-slate-400 text-sm flex items-center gap-2">
                              <Lock className="h-4 w-4" />
                              Complete el checklist Comercial (100%) para desbloquear esta fase
                            </div>
                          )}
                          
                          {/* Select All Checkbox */}
                          <div className="flex items-center gap-3 p-2 rounded-lg bg-[#0A0A35]/50 border border-[#1E1E5F]">
                            <Checkbox
                              checked={selectedProvision.administrativa_checklist?.every(item => item.completed)}
                              onCheckedChange={(checked) => handleToggleAllChecklist("administrativa", checked)}
                              className="border-yellow-400 data-[state=checked]:bg-yellow-500"
                              disabled={!canEditTab("administrativa")}
                            />
                            <span className="text-yellow-400 font-medium text-sm">Seleccionar Todos</span>
                            {!canEditTab("administrativa") && <Lock className="h-3 w-3 text-slate-500 ml-auto" />}
                          </div>
                          
                          {selectedProvision.administrativa_checklist?.map((item, index) => (
                            <div 
                              key={item.task_id}
                              className={`p-3 rounded-lg border ${item.completed ? "bg-yellow-500/10 border-yellow-500/30" : "bg-[#0A0A35] border-[#1E1E5F]"}`}
                            >
                              <div className="flex items-start gap-3">
                                <Checkbox
                                  checked={item.completed}
                                  onCheckedChange={(checked) => handleChecklistUpdate("administrativa", index, checked)}
                                  className="mt-1 border-yellow-400 data-[state=checked]:bg-yellow-500"
                                  disabled={!canEditTab("administrativa")}
                                />
                                <div className="flex-1">
                                  <p className={`font-medium ${item.completed ? "text-yellow-400 line-through" : "text-white"}`}>
                                    {item.task_name}
                                  </p>
                                  {item.completed && item.completed_by_name && (
                                    <p className="text-xs text-slate-400 mt-1">
                                      Completado por {item.completed_by_name} el {formatDate(item.completed_at)}
                                    </p>
                                  )}
                                  <Textarea
                                    placeholder="Comentarios (opcional)"
                                    value={item.comments || ""}
                                    onChange={(e) => {
                                      const updated = [...selectedProvision.administrativa_checklist];
                                      updated[index] = { ...updated[index], comments: e.target.value };
                                      setSelectedProvision({ ...selectedProvision, administrativa_checklist: updated });
                                    }}
                                    onBlur={() => handleChecklistUpdate("administrativa", index, item.completed, selectedProvision.administrativa_checklist[index].comments)}
                                    className="mt-2 bg-[#0A0A35] border-[#1E1E5F] text-white text-sm h-16"
                                    disabled={!canEditTab("administrativa")}
                                  />
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                      <div className="mt-4">
                        <Progress value={selectedProvision.administrativa_progress} className="h-2" />
                        <p className="text-sm text-slate-400 mt-1 text-right">
                          {selectedProvision.administrativa_progress}% completado
                        </p>
                      </div>
                      
                      {selectedProvision.administrativa_progress < 100 && (
                        <div className="mt-4 p-3 rounded-lg bg-orange-500/10 border border-orange-500/30">
                          <p className="text-orange-400 text-sm flex items-center gap-2">
                            <Lock className="h-4 w-4" />
                            Los tickets técnicos permanecen bloqueados hasta completar esta fase al 100%
                          </p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                {/* Technical Phase - Checklist from Master */}
                <TabsContent value="tecnica" className="mt-4">
                  <Card className="bg-[#1B1B4B]/30 border-[#1E1E5F]">
                    <CardHeader>
                      <CardTitle className="text-orange-400 text-lg flex items-center gap-2 justify-between">
                        <div className="flex items-center gap-2">
                          <Wrench className="h-5 w-5" />
                          Fase Técnica — Instalación de Servicios
                        </div>
                        {(() => {
                          const items = selectedProvision.technical_items || [];
                          const installed = items.filter(i => i.status === "INSTALADO").length;
                          return items.length > 0 && (
                            <Badge data-testid="tech-progress-badge" className={`${installed === items.length ? "bg-green-500/20 text-green-400" : "bg-orange-500/20 text-orange-300"} text-sm`}>
                              Servicios instalados: {installed} / {items.length}
                            </Badge>
                          );
                        })()}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {selectedProvision.status === "PENDIENTE_TECNICA" && selectedProvision.rollback_reason && (
                        <div className="mb-4 p-3 rounded-lg bg-orange-500/20 border border-orange-500/50">
                          <p className="text-orange-400 text-sm font-medium flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Motivo del retroceso:
                          </p>
                          <p className="text-white mt-1">{selectedProvision.rollback_reason}</p>
                        </div>
                      )}

                      {(!selectedProvision.technical_items || selectedProvision.technical_items.length === 0) ? (
                        <div className="text-center py-4">
                          <p className="text-slate-400">No hay items técnicos para esta provisión.</p>
                          <p className="text-xs text-slate-500 mt-1">Los items técnicos se generan automáticamente al crear la provisión.</p>
                        </div>
                      ) : (
                        <div className="space-y-4">
                          {/* Group by product_type */}
                          {[...new Set(selectedProvision.technical_items.map(i => i.product_type || "General"))].map(pt => {
                            const groupItems = selectedProvision.technical_items.filter(i => (i.product_type || "General") === pt);
                            return (
                              <div key={pt} className="space-y-2">
                                <div className="flex items-center gap-2 pb-1 border-b border-[#1E1E5F]">
                                  <Package className="h-4 w-4 text-orange-300" />
                                  <span className="text-orange-300 font-medium text-sm uppercase">{pt}</span>
                                </div>
                                {groupItems.map(item => {
                                  const isExpanded = expandedItems[item.id];
                                  const completedTasks = (item.checklist || []).filter(t => t.completed).length;
                                  const totalTasks = (item.checklist || []).length;
                                  const isInstalled = item.status === "INSTALADO";
                                  const canInstall = item.checklist_progress >= 100 || totalTasks === 0;
                                  const displayName = item.linea_numero
                                    ? `${item.service_name} (línea ${item.linea_numero})`
                                    : item.service_name;

                                  return (
                                    <div key={item.id} data-testid={`tech-item-${item.id}`}
                                      className={`rounded-lg border ${isInstalled ? "bg-green-500/10 border-green-500/30" : "bg-[#0A0A35] border-[#1E1E5F]"}`}>
                                      {/* Card header — click to expand */}
                                      <div className="flex items-center justify-between p-3 cursor-pointer"
                                        onClick={() => toggleItemExpanded(item.id)}>
                                        <div className="flex items-center gap-3 flex-1 min-w-0">
                                          <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform shrink-0 ${isExpanded ? "rotate-90" : ""}`} />
                                          <div className="min-w-0">
                                            <p className={`font-medium truncate ${isInstalled ? "text-green-400" : "text-white"}`}>{displayName}</p>
                                            {totalTasks > 0 && (
                                              <p className="text-xs text-slate-400 mt-0.5">
                                                {completedTasks}/{totalTasks} tareas
                                              </p>
                                            )}
                                          </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                          {isInstalled ? (
                                            <Badge data-testid={`badge-installed-${item.id}`} className="bg-green-500/20 text-green-400">
                                              INSTALADO {item.fecha_instalacion}
                                            </Badge>
                                          ) : (
                                            <Badge className={`${item.status === "EN_PROGRESO" ? "bg-blue-500/20 text-blue-400" : "bg-yellow-500/20 text-yellow-400"}`}>
                                              {item.status === "EN_PROGRESO" ? "EN PROGRESO" : "PENDIENTE"}
                                            </Badge>
                                          )}
                                        </div>
                                      </div>

                                      {/* Expanded: checklist + install button */}
                                      {isExpanded && (
                                        <div className="px-3 pb-3 pt-0 border-t border-[#1E1E5F]">
                                          {totalTasks > 0 ? (
                                            <div className="space-y-2 mt-2">
                                              {item.checklist.map(task => (
                                                <div key={task.task_id} className="flex items-start gap-3 p-2 rounded bg-[#0F0F3D]/50">
                                                  <Checkbox
                                                    data-testid={`check-${item.id}-${task.task_id}`}
                                                    checked={task.completed}
                                                    disabled={isInstalled}
                                                    onCheckedChange={(checked) => handleTechnicalItemChecklistUpdate(item.id, task.task_id, checked)}
                                                    className="mt-0.5 border-orange-400 data-[state=checked]:bg-orange-500"
                                                  />
                                                  <div className="flex-1">
                                                    <p className={`text-sm ${task.completed ? "text-orange-400 line-through" : "text-white"}`}>{task.task_name}</p>
                                                    {task.completed_by_name && (
                                                      <p className="text-xs text-slate-500 mt-0.5">
                                                        {task.completed_by_name} — {task.completed_at ? new Date(task.completed_at).toLocaleDateString("es-ES") : ""}
                                                      </p>
                                                    )}
                                                  </div>
                                                </div>
                                              ))}
                                            </div>
                                          ) : (
                                            <p className="text-xs text-slate-500 mt-2">Sin tareas de checklist para este tipo de servicio.</p>
                                          )}
                                          {(() => {
                                            // Multi-bono per extension: prefer `bonos_asociados` list;
                                            // fall back to legacy singleton `bono_asociado`.
                                            const bonos = Array.isArray(item.bonos_asociados) && item.bonos_asociados.length > 0
                                              ? item.bonos_asociados
                                              : (item.bono_asociado ? [item.bono_asociado] : []);
                                            if (bonos.length === 0) return null;
                                            return (
                                              <div className="mt-3 p-3 rounded-lg bg-[#1B1B4B]/30 border border-cyan-500/20 space-y-1">
                                                <p className="text-cyan-300 text-sm font-medium flex items-center gap-2">
                                                  <Package className="h-4 w-4" />
                                                  {bonos.length === 1 ? "Bono asociado" : `Bonos asociados (${bonos.length})`}
                                                </p>
                                                {bonos.map((b, bi) => (
                                                  <div key={b.linea_id || bi} className="pl-6 text-sm" data-testid={`bono-asoc-${item.id}-${bi}`}>
                                                    <span className="text-cyan-200">└─ {b.service_name}</span>
                                                    {b.installed ? (
                                                      <span className="text-green-400 text-xs ml-2">Instalado — {b.fecha_instalacion}</span>
                                                    ) : (
                                                      <span className="text-yellow-400 text-xs ml-2">Se instala junto con la línea</span>
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            );
                                          })()}
                                          {!isInstalled && (
                                            <Button
                                              data-testid={`install-btn-${item.id}`}
                                              disabled={!canInstall}
                                              onClick={(e) => { e.stopPropagation(); setInstallItemId(item.id); setInstallDate(""); setInstallModalOpen(true); }}
                                              className="mt-3 w-full bg-green-600 hover:bg-green-700 disabled:opacity-40"
                                              title={!canInstall ? `Completa las ${totalTasks - completedTasks} tareas restantes` : ""}
                                            >
                                              <CheckCircle2 className="h-4 w-4 mr-2" />
                                              {(() => {
                                                const bonoCount = Array.isArray(item.bonos_asociados) && item.bonos_asociados.length > 0
                                                  ? item.bonos_asociados.length
                                                  : (item.bono_asociado ? 1 : 0);
                                                if (bonoCount === 0) return "Marcar como Instalado";
                                                if (bonoCount === 1) return "Marcar línea + bono como Instalados";
                                                return `Marcar línea + ${bonoCount} bonos como Instalados`;
                                              })()}
                                            </Button>
                                          )}
                                          {isInstalled && item.installed_by_name && (
                                            <p className="text-xs text-slate-500 mt-2">
                                              Instalado por {item.installed_by_name}
                                            </p>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  );
                                })}
                              </div>
                            );
                          })}

                          {/* Complete Provision button */}
                          {selectedProvision.status === "PENDIENTE_TECNICA" && (() => {
                            const items = selectedProvision.technical_items || [];
                            const pending = items.filter(i => i.status !== "INSTALADO").length;
                            return (
                              <div className="pt-4 border-t border-[#1E1E5F]">
                                <Button
                                  data-testid="complete-provision-btn"
                                  disabled={pending > 0}
                                  onClick={handleCompleteProvision}
                                  className="w-full bg-green-600 hover:bg-green-700 disabled:opacity-40"
                                  title={pending > 0 ? `Quedan ${pending} servicios sin instalar` : ""}
                                >
                                  <CheckCircle2 className="h-4 w-4 mr-2" />
                                  {pending > 0 ? `Completar Provisión (${pending} pendientes)` : "Completar Provisión"}
                                </Button>
                              </div>
                            );
                          })()}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>
              </Tabs>
            </div>
          )}
        </DialogContent>
      </Dialog>
  );
};
